import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { PresenceProvider } from './context/PresenceContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard'; // kept for backward compat
import Clienti from './pages/Clienti';
import ClientiAttivi from './pages/ClientiAttivi';
import ClienteDetail from './pages/ClienteDetail';
import Calendario from './pages/Calendario';
import Task from './pages/Task';
import Admin from './pages/Admin';
import Segnala from './pages/Segnala';
import Leads from './pages/Leads';
import PianoEditoriale from './pages/PianoEditoriale';
import ClientVault from './pages/ClientVault';
import CosaFunziona from './pages/CosaFunziona';
import EdenHQ from './pages/EdenHQ';
import AgentiVivi from './pages/AgentiVivi';
import Team from './pages/Team';
import Direttori from './pages/Direttori';
import B2BDistribution from './pages/B2BDistribution';
import PepSchede from './pages/PepSchede';
import Servers from './pages/Servers';
import StatusMcp from './pages/StatusMcp';
import Abbonamenti from './pages/Abbonamenti';
import Gamification from './pages/Gamification';
import MetaAnalytics from './pages/MetaAnalytics';
import Scadenzario from './pages/Scadenzario';
import Fatturazione from './pages/Fatturazione';
import FatturaElettronica from './pages/FatturaElettronica';
import Agenzia from './pages/Agenzia';
import AgendaOggi from './pages/AgendaOggi';
import OnboardingWizard from './pages/OnboardingWizard';
import ClientPortal from './pages/ClientPortal';
import Pubblicazioni from './pages/Pubblicazioni';
import PedContenuto from './pages/PedContenuto';
import Cockpit from './pages/Cockpit';
import Audit from './pages/Audit';
import Produzione from './pages/Produzione';
import { RUOLI_ANALYTICS } from './lib/ruoli';
import Studio from './pages/Studio';
import Guida from './pages/Guida';
import GuidaTrend from './pages/GuidaTrend';
import Trend from './pages/Trend';
import { useWorkspace } from './hooks/useWorkspace';

function ProtectedRoute({ children, roles }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.ruolo)) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

// Videomaker e Social Media Manager atterrano sulla loro bacheca "Il mio lavoro";
// la Home classica resta disponibile su /panoramica.
function HomeOrStudio() {
  const { focus } = useWorkspace();
  return focus ? <Studio /> : <Home />;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login"    element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/register" element={<Register />} />
      {/* Portale cliente pubblico — nessun login, accesso via token */}
      <Route path="/portal/:token" element={<ClientPortal />} />

      {/* Home "Solo per me" */}
      <Route path="/" element={<ProtectedRoute><HomeOrStudio /></ProtectedRoute>} />
      <Route path="/panoramica" element={<ProtectedRoute><Home /></ProtectedRoute>} />
      <Route path="/studio" element={<ProtectedRoute><Studio /></ProtectedRoute>} />
      <Route path="/guida" element={<ProtectedRoute><Guida /></ProtectedRoute>} />
      <Route path="/guida-trend" element={<ProtectedRoute><GuidaTrend /></ProtectedRoute>} />
      <Route path="/trend" element={<ProtectedRoute><Trend /></ProtectedRoute>} />

      {/* Operativo */}
      <Route path="/clienti-attivi" element={<ProtectedRoute><ClientiAttivi /></ProtectedRoute>} />
      <Route path="/clienti/:id"    element={<ProtectedRoute><ClienteDetail /></ProtectedRoute>} />
      <Route path="/clienti"        element={<ProtectedRoute><Clienti /></ProtectedRoute>} />
      <Route path="/leads"          element={<ProtectedRoute><Leads /></ProtectedRoute>} />
      <Route path="/task"           element={<ProtectedRoute><Task /></ProtectedRoute>} />
      <Route path="/calendario"     element={<ProtectedRoute><Calendario /></ProtectedRoute>} />
      <Route path="/ped"            element={<ProtectedRoute><PianoEditoriale /></ProtectedRoute>} />
      <Route path="/ped-storie"     element={<ProtectedRoute><PianoEditoriale /></ProtectedRoute>} />
      <Route path="/campagne"       element={<ProtectedRoute><PianoEditoriale /></ProtectedRoute>} />
      <Route path="/client-vault"   element={<ProtectedRoute><ClientVault /></ProtectedRoute>} />
      <Route path="/ped/contenuto/:id" element={<ProtectedRoute><PedContenuto /></ProtectedRoute>} />
      <Route path="/pubblicazioni"  element={<ProtectedRoute><Pubblicazioni /></ProtectedRoute>} />

      {/* Agenzia */}
      <Route path="/b2b"          element={<ProtectedRoute><B2BDistribution /></ProtectedRoute>} />
      <Route path="/direttori"   element={<ProtectedRoute><Direttori /></ProtectedRoute>} />
      <Route path="/pep-schede"  element={<ProtectedRoute><PepSchede /></ProtectedRoute>} />
      <Route path="/eden-hq"      element={<ProtectedRoute><EdenHQ /></ProtectedRoute>} />
      <Route path="/gamification" element={<ProtectedRoute><Gamification /></ProtectedRoute>} />
      <Route path="/agenti-vivi" element={<ProtectedRoute><AgentiVivi /></ProtectedRoute>} />
      <Route path="/team"        element={<ProtectedRoute><Team /></ProtectedRoute>} />

      {/* Sistema */}
      <Route path="/segnala"      element={<ProtectedRoute><Segnala /></ProtectedRoute>} />
      <Route path="/admin"        element={<ProtectedRoute roles={['admin']}><Admin /></ProtectedRoute>} />
      <Route path="/audit"        element={<ProtectedRoute roles={['admin']}><Audit /></ProtectedRoute>} />
      <Route path="/servers"      element={<ProtectedRoute roles={['admin']}><Servers /></ProtectedRoute>} />
      <Route path="/status-mcp"   element={<ProtectedRoute roles={['admin']}><StatusMcp /></ProtectedRoute>} />
      <Route path="/abbonamenti"  element={<ProtectedRoute roles={['admin']}><Abbonamenti /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute roles={RUOLI_ANALYTICS}><MetaAnalytics /></ProtectedRoute>} />
      <Route path="/cosa-funziona" element={<ProtectedRoute roles={RUOLI_ANALYTICS}><CosaFunziona /></ProtectedRoute>} />
      <Route path="/produzione" element={<ProtectedRoute><Produzione /></ProtectedRoute>} />
      <Route path="/oggi"        element={<ProtectedRoute><AgendaOggi /></ProtectedRoute>} />
      <Route path="/onboarding"  element={<ProtectedRoute><OnboardingWizard /></ProtectedRoute>} />
      <Route path="/scadenzario" element={<ProtectedRoute><Scadenzario /></ProtectedRoute>} />
      <Route path="/fatturazione" element={<ProtectedRoute roles={['admin']}><Fatturazione /></ProtectedRoute>} />
      <Route path="/fattura-elettronica" element={<ProtectedRoute roles={['admin']}><FatturaElettronica /></ProtectedRoute>} />
      <Route path="/agenzia" element={<ProtectedRoute roles={['admin']}><Agenzia /></ProtectedRoute>} />
      <Route path="/cockpit" element={<ProtectedRoute roles={['admin']}><Cockpit /></ProtectedRoute>} />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <PresenceProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </PresenceProvider>
      </ToastProvider>
    </AuthProvider>
  );
}
